import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, Check, ChevronDown, Loader2, Medal, Trophy, Award, X, Zap, MessageCircle, MessageSquare, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { FadeUp, MaskedLine } from "../components/motion";
import { ELink } from "../components/Editable";
import { CallRequestDialog } from "../components/CallRequestDialog";
import { SEO } from "../components/SEO";
import { api, formatApiError, trackClicSoumission } from "../lib/api";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

const fmt = (n) => n.toFixed(2).replace(".", ",") + " $";

// Prix unitaire du produit au palier correspondant à la quantité (rabais de volume)
const unitAt = (p, n) => {
  if (!p) return 0;
  let price = p.base_price;
  (p.tiers || []).forEach((tr) => { if (n >= tr.min_qty && tr.price) price = tr.price; });
  return price;
};

const SPORTS = ["Hockey", "Soccer", "Football", "Basketball", "Volleyball", "Natation", "Baseball", "Golf", "Danse"]
  .sort((a, b) => a.localeCompare(b, "fr")).concat("Autre");  // alphabétique, « Autre » en fin
// Bande des sports dans chaque carte forfait — « Autre » inclus : le visiteur
// peut alors inscrire son sport en texte libre dans le formulaire
const MARQUEE_SPORTS = SPORTS;

export default function Forfaits() {
  const { lang, t, ts } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [players, setPlayers] = useState(12);  // minimum officiel : 12 joueurs partout (demande du propriétaire)
  const [chosen, setChosen] = useState(null);          // forfait sélectionné pour la soumission
  const [form, setForm] = useState({ phone: "", organization: "", city: "", sport: "Hockey", customSport: "", date: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [cardSport, setCardSport] = useState({});    // sport choisi par carte via la bande des sports
  const [bandOpen, setBandOpen] = useState({});      // bande des sports dépliée par carte
  const [sportImgs, setSportImgs] = useState({});    // cache : produits de la boutique par sport
  const [popup, setPopup] = useState(null);          // produit dont la mini-fiche est ouverte (popup boutique)
  const formRef = useRef(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const presetDone = useRef(false);                  // présélection initiale faite une seule fois

  // Retour d'inscription/connexion (le bouton « Recevoir ma soumission » y mène les
  // visiteurs non connectés) : on restaure le brouillon — rien à retaper.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("forfait_draft");
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.form) setForm((f) => ({ ...f, ...d.form }));
      if (d.players) setPlayers(d.players);
      if (d.chosen) setChosen(d.chosen);
      sessionStorage.removeItem("forfait_draft");
    } catch { /* brouillon illisible : ignoré */ }
  }, []);

  useEffect(() => {
    api.get("/forfaits").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  const trophy = data?.trophy;
  const bundles = data?.medal_bundles || {};
  const photos = data?.photos || {};   // photos des cartes, choisies par l'admin (1/2/4)

  // Prix de la part médaille : PRIX FIXE officiel par forfait — AUCUN rabais de
  // quantité automatique (demande client) ; la mention « Les prix peuvent varier
  // selon la quantité » est affichée et le prix final est confirmé à la soumission.
  const medalUnitAt = (pkgId) => bundles[pkgId]?.price || 0;
  const trophyPrice = trophy ? trophy.base_price + trophy.gravure : 0;

  const bmin = (id) => bundles[id]?.min_players || 12;  // repli : minimum officiel 12
  const eff = (id) => Math.max(players, bmin(id));  // quantité facturée (minimum du forfait)

  // Les 3 forfaits officiels : PRIX FIXE par joueur (quantité minimale par forfait).
  // Bronze/Argent = médaille · Argent ajoute 1 trophée d'équipe au choix ·
  // Or = 1 trophée par joueur (prix fixe), sans médaille.
  // Pluriel propre sur les quantités (demande du propriétaire) : « 12 × Médailles 2 po… »
  const itemTxt = (key, n) => ts(`forfaits.${key}${Number(n) > 1 ? "_pl" : ""}`);
  const packs = bundles.participation ? [
    { id: "participation", icon: Medal, name: t("forfaits.p1_name"), tag: t("forfaits.p1_tag"),
      min: bmin("participation"), unit: medalUnitAt("participation"), imgs: photos.participation || [],
      items: [`${eff("participation")} × ${itemTxt("p1_item_medal", eff("participation"))}`],
      total: eff("participation") * medalUnitAt("participation"), popular: false },
    { id: "classique", icon: Trophy, name: t("forfaits.p2_name"), tag: t("forfaits.p2_tag"),
      min: bmin("classique"), unit: medalUnitAt("classique"), imgs: photos.classique || [],
      items: [`${eff("classique")} × ${itemTxt("p2_item_medal", eff("classique"))}`,
              t("forfaits.item_trophy_choice")],
      total: eff("classique") * medalUnitAt("classique") + trophyPrice, popular: true },
    { id: "prestige", icon: Award, name: t("forfaits.p3_name"), tag: t("forfaits.p3_tag"),
      min: bmin("prestige"), unit: medalUnitAt("prestige"), imgs: photos.prestige || [],
      items: [`${eff("prestige")} × ${itemTxt("p3_item_trophy_each", eff("prestige"))}`],
      total: eff("prestige") * medalUnitAt("prestige"), popular: false },
  ] : [];

  const choose = (id) => {
    setChosen(id);
    setDone(false);
    const pk = packs.find((p) => p.id === id);
    if (pk && players < pk.min) setPlayers(pk.min);  // hisse au minimum du forfait choisi
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  // Photos de la boutique pour un sport : médailles (4 premiers de chaque bande)
  // et trophées (4 suivants) séparés — ordre garanti par l'API /products/picks.
  // On garde les PRODUITS complets (id, nom, prix) : les miniatures ouvrent un
  // popup de la fiche boutique (demande client).
  const fetchSportImgs = async (s) => {
    // PAS de cache : nouvelle sélection aléatoire à chaque appel (demande client).
    // On re-brasse côté client pour que les 3 cartes ne montrent pas les mêmes produits.
    try {
      const r = await api.get("/products/picks", { params: { sport: s } });
      const strips = r.data.strips || [];
      const sh = (arr) => [...arr].sort(() => Math.random() - 0.5);
      const medals = sh(strips.flatMap((st) => st.slice(0, 4)).filter((x) => x.image));
      const trophies = sh(strips.flatMap((st) => st.slice(4)).filter((x) => x.image));
      setSportImgs((si) => ({ ...si, [s]: { medals, trophies } }));
    } catch { /* on garde les photos du forfait */ }
  };

  // Présélection d'un sport sur LES 3 cartes : arrivée depuis une page ville
  // (?sport=hockey) ou sport de saison réglé dans l'admin — pour attirer l'œil
  const applySportAll = (s) => {
    if (!SPORTS.includes(s)) return;
    setCardSport({ participation: s, classique: s, prestige: s });
    setForm((f) => ({ ...f, sport: s }));
    if (s !== "Autre") fetchSportImgs(s);
  };

  useEffect(() => {
    if (!data || presetDone.current) return;
    presetDone.current = true;
    const q = searchParams.get("sport");           // priorité au lien (pages villes)
    const s = SPORTS.includes(q) ? q
      : (SPORTS.includes(data.season_sport) ? data.season_sport : null);
    if (s) applySportAll(s);
    else fetchSportImgs("");   // sans sport présélectionné : vignettes aléatoires (calendrier des saisons)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Clic sur un sport de la bande : les miniatures de la carte affichent des
  // produits de la boutique liés à ce sport (aléatoires) ET le sport est
  // présélectionné dans le formulaire. Re-clic sur le même sport = photos
  // d'origine et bande vide à nouveau.
  const pickSport = async (pkgId, s) => {
    const deselect = cardSport[pkgId] === s;
    setCardSport({ ...cardSport, [pkgId]: deselect ? null : s });
    setBandOpen({ ...bandOpen, [pkgId]: false });   // la bande se replie après le choix
    if (!deselect) setForm((f) => ({ ...f, sport: s }));
    if (!deselect && s === "Autre") {
      // « Autre » : pas de photo de sport à chercher — on garde les photos du
      // forfait et on amène le visiteur au formulaire pour inscrire son sport
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
    if (!deselect && s !== "Autre") fetchSportImgs(s);
  };

  // Miniatures affichées : composition officielle — Bronze 1 médaille ·
  // Argent 1 médaille + 3 trophées · Or 4 trophées (sans médaille). Les prises
  // sont DÉCALÉES par carte (mo/to) pour que chaque forfait montre des produits
  // différents. Sans sport choisi : bassin aléatoire du jour (calendrier).
  const SPORT_MIX = {
    participation: { m: 1, t: 0, mo: 0, to: 0 },
    classique: { m: 1, t: 3, mo: 1, to: 0 },
    prestige: { m: 0, t: 4, mo: 2, to: 4 },
  };
  const cardImgs = (p) => {
    const s = cardSport[p.id];
    const c = (s && sportImgs[s]) || sportImgs[""];
    if (c && (c.medals.length || c.trophies.length)) {
      const mix = SPORT_MIX[p.id] || { m: 1, t: 1, mo: 0, to: 0 };
      let imgs = [...c.medals.slice(mix.mo, mix.mo + mix.m), ...c.trophies.slice(mix.to, mix.to + mix.t)];
      const want = mix.m + mix.t;
      if (imgs.length < want) {   // pas assez d'un type : on complète avec le reste du bassin
        const pool = [...c.medals, ...c.trophies];
        imgs = imgs.concat(pool.filter((x) => !imgs.some((y) => y.id === x.id)).slice(0, want - imgs.length));
      }
      if (imgs.length) return imgs;
    }
    return p.imgs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen) {
      // Bouton volontairement NON désactivé : un clic sans forfait choisi guide
      // vers les cartes au lieu de « ne rien faire » (rétroaction claire)
      toast.error(lang === "fr" ? "Choisissez d'abord un forfait ci-dessus (Bronze, Argent ou Or)" : "Pick a package above first (Bronze, Silver or Gold)");
      document.querySelector('[data-testid="forfait-card-participation"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!user) {
      // Visiteur non connecté : le bouton mène à la création de compte gratuit —
      // le brouillon est conservé et restauré au retour (demande du propriétaire)
      try {
        sessionStorage.setItem("forfait_draft", JSON.stringify({ form, players, chosen }));
      } catch { /* stockage indisponible : le formulaire sera vide au retour */ }
      toast(lang === "fr"
        ? "Créez votre compte gratuit (1 minute) — votre demande sera conservée."
        : "Create your free account (1 minute) — your request will be kept.");
      navigate("/inscription", { state: { next: "/forfaits" } });
      return;
    }
    if (form.phone.trim().length < 7) {
      toast.error(lang === "fr" ? "Le téléphone est requis" : "Phone is required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/forfaits/soumission", {
        package: chosen, players: eff(chosen),   // minimum du forfait appliqué
        phone: form.phone, organization: form.organization,
        city: form.city, date: form.date,
        // « Autre » : le client précise son sport en texte libre
        sport: form.sport === "Autre" && form.customSport.trim() ? form.customSport.trim() : form.sport,
      });
      setDone(true);
      toast.success(t("forfaits.form_done_title"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const ldNames = { participation: "forfaits.p1_name", classique: "forfaits.p2_name", prestige: "forfaits.p3_name" };
  const ld = {
    "@context": "https://schema.org", "@type": "OfferCatalog",
    name: ts("forfaits.seo_title"),
    itemListElement: packs.map((p) => ({
      "@type": "Offer", name: ts(ldNames[p.id]),   // ts : toujours du texte brut (JSON-LD)
      price: p.total.toFixed(2), priceCurrency: "CAD",
    })),
  };

  return (
    <div data-testid="forfaits-page" className="pt-32 pb-28">
      <SEO title={ts("forfaits.seo_title")} description={ts("forfaits.seo_desc")} path="/forfaits" jsonLd={ld} />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("forfaits.kicker")}</p>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight mb-6">
            <MaskedLine key={ts("forfaits.title_1")} delay={0.15}>{t("forfaits.title_1")}</MaskedLine>{" "}
            <MaskedLine key={ts("forfaits.title_2")} delay={0.35} className="italic text-gold">{t("forfaits.title_2")}</MaskedLine>
          </h1>
          <p className="text-ivory/60 max-w-2xl leading-relaxed">{t("forfaits.intro")}</p>
        </FadeUp>

        {/* Sélecteur du nombre de joueurs — tous les prix suivent en direct */}
        <FadeUp delay={0.1}>
          <div className="mt-10 bg-surface border border-gold/20 rounded-2xl p-7" data-testid="forfaits-players">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <label htmlFor="players-range" className="text-[11px] uppercase tracking-widest text-ivory/65">
                {t("forfaits.players_label")}
              </label>
              <span className="font-serif text-3xl text-gold" data-testid="forfaits-players-count">
                {players} <span className="text-sm text-ivory/55">{t("forfaits.players_unit")}</span>
              </span>
            </div>
            <input
              id="players-range" type="range" min="6" max="100" step="1" value={players}
              data-testid="forfaits-players-range"
              onChange={(e) => setPlayers(Number(e.target.value))}
              className="w-full accent-[#d4af37]"
            />
            <p className="text-xs text-ivory/45 mt-3">{t("forfaits.volume_note")}</p>
          </div>
        </FadeUp>

        {/* Les 3 forfaits */}
        {!data ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : !bundles.participation ? (
          <p className="text-ivory/60 py-16 text-center">…</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {packs.map((p, i) => (
              <FadeUp key={p.id} delay={0.08 * i}>
                <div
                  data-testid={`forfait-card-${p.id}`}
                  onClick={() => choose(p.id)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") choose(p.id); }}
                  title={lang === "fr" ? "Cliquer pour choisir ce forfait" : "Click to pick this package"}
                  className={`relative h-full bg-surface rounded-2xl p-7 border transition-colors duration-300 flex flex-col cursor-pointer ${
                    chosen === p.id ? "border-gold shadow-[0_0_24px_rgba(212,175,55,0.15)]"
                    : p.popular ? "border-gold/40" : "border-ivory/10 hover:border-gold/30"}`}
                >
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-ink text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full">
                      {t("forfaits.popular")}
                    </span>
                  )}
                  <p.icon className="w-8 h-8 text-gold mb-4" />
                  <h2 className="font-serif text-2xl mb-1">{p.name}</h2>
                  <p className="text-ivory/55 text-sm mb-4">{p.tag}</p>
                  {/* Photos des produits du forfait (médaille/trophée de référence,
                      changeables par l'admin dans l'aperçu) */}
                  {cardImgs(p).length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-4" data-testid={`forfait-photos-${p.id}`}>
                      {cardImgs(p).map((it, j) => (
                        <button key={j} type="button" onClick={(e) => { e.stopPropagation(); setPopup(it); }}
                          data-testid={`forfait-photo-${p.id}-${j}`}
                          title={lang === "fr" ? "Voir la fiche boutique" : "View shop page"}
                          className="rounded-xl border border-gold/25 bg-ink overflow-hidden hover:border-gold hover:shadow-[0_0_14px_rgba(212,175,55,0.25)] transition-all duration-300 cursor-pointer">
                          <img src={it.image} alt="" loading="lazy" className="w-24 h-24 object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Bande des sports — FIXE et VIDE au départ : un clic dessus
                      dévoile la liste ; le choix change les miniatures de la carte
                      et présélectionne le sport du formulaire */}
                  <p className="text-[10px] uppercase tracking-widest text-white mb-1">
                    {lang === "fr" ? "Choisissez votre sport" : "Choose your sport"}
                  </p>
                  <div className="border-y border-gold/15 mb-5"
                    data-testid={`forfait-sports-marquee-${p.id}`}>
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); setBandOpen({ ...bandOpen, [p.id]: !bandOpen[p.id] }); }}
                      data-testid={`forfait-sports-toggle-${p.id}`}
                      className={`w-full flex items-center justify-between text-left text-[11px] uppercase tracking-widest py-2.5 transition-colors duration-200 cursor-pointer ${
                        cardSport[p.id] ? "text-gold font-bold" : "text-gold/70 hover:text-gold"}`}
                    >
                      <span>{cardSport[p.id] || "Sport"}</span>
                      <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${bandOpen[p.id] ? "rotate-180" : ""}`} />
                    </button>
                    {bandOpen[p.id] && (
                      <div className="pb-1" data-testid={`forfait-sports-list-${p.id}`}>
                        {MARQUEE_SPORTS.map((s) => (
                          <button key={s} type="button" onClick={(e) => { e.stopPropagation(); pickSport(p.id, s); }}
                            data-testid={`forfait-sport-pick-${p.id}-${s.toLowerCase()}`}
                            className={`block w-full text-left text-[11px] uppercase tracking-widest py-[7px] transition-colors duration-200 cursor-pointer ${
                              cardSport[p.id] === s ? "text-gold font-bold" : "text-gold/70 hover:text-gold"}`}
                          >
                            <span className="text-gold/30 mr-2">•</span>{s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="font-serif text-4xl text-gold mb-1" data-testid={`forfait-price-${p.id}`}>{fmt(p.unit)}</p>
                  <p className="text-ivory/50 text-xs mb-1">
                    {t("forfaits.per_player")} · {t("forfaits.min_players", { n: p.min })}
                  </p>
                  <p className="text-champagne/85 text-sm mb-1" data-testid={`forfait-total-${p.id}`}>
                    {t("forfaits.total")} : {fmt(p.total)}
                  </p>
                  <p className="text-ivory/45 text-[11px] italic mb-5" data-testid={`forfait-varies-${p.id}`}>{t("forfaits.volume_note")}</p>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {p.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-ivory/75">
                        <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />{it}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => choose(p.id)}
                    data-testid={`forfait-cta-${p.id}`}
                    className={`w-full inline-flex items-center justify-center gap-2 font-bold py-3.5 rounded-full text-sm transition-all duration-300 ${
                      chosen === p.id ? "bg-gold text-ink"
                      : "border border-gold/50 text-gold hover:bg-gold hover:text-ink"}`}
                  >
                    {t("forfaits.cta")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </FadeUp>
            ))}
          </div>
        )}

        {/* Formulaire de soumission */}
        <FadeUp delay={0.1}>
          <div ref={formRef} className="mt-16 bg-surface border border-gold/20 rounded-2xl p-7 md:p-10 scroll-mt-28" data-testid="forfaits-form">
            {done ? (
              <div className="text-center py-8">
                <BadgeCheck className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="font-serif text-3xl mb-2" data-testid="forfaits-done-title">{t("forfaits.form_done_title")}</h3>
                <p className="text-ivory/60 max-w-md mx-auto">{t("forfaits.form_done")}</p>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-3xl mb-1">{t("forfaits.form_title")}</h3>
                <p className="text-ivory/50 text-sm mb-2">
                  {t("forfaits.cta_sub")}
                  {chosen && (
                    <span className="text-gold"> — {t("forfaits.chosen")} : <strong>{packs.find((p) => p.id === chosen)?.name}</strong></span>
                  )}
                </p>
                {user ? (
                  <p className="text-xs text-ivory/60 mb-7" data-testid="forfaits-account">
                    {t("forfaits.account_line")} : <strong className="text-champagne">{user.name || user.email}</strong>
                    {user.name ? ` (${user.email})` : ""}
                  </p>
                ) : (
                  <p className="text-xs text-gold/90 mb-7" data-testid="forfaits-account-guest">
                    {lang === "fr"
                      ? "Sans compte ? Cliquez « Recevoir ma soumission » — la création de compte gratuit (1 minute) s'ouvrira et votre demande sera conservée."
                      : "No account? Click “Get my quote” — free account creation (1 minute) will open and your request will be kept."}
                  </p>
                )}
                {!chosen && (
                  <p className="text-amber-400/90 text-xs mb-5" data-testid="forfaits-choose-first">
                    {lang === "fr" ? "↑ Choisissez d'abord un forfait ci-dessus" : "↑ Pick a package above first"}
                  </p>
                )}
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                    placeholder={ts("forfaits.form_phone")} data-testid="forfaits-input-phone" type="tel"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    placeholder={ts("forfaits.form_org")} data-testid="forfaits-input-org"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder={ts("forfaits.form_city")} data-testid="forfaits-input-city"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })}
                    data-testid="forfaits-input-sport"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none">
                    {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {form.sport === "Autre" && (
                    <input required value={form.customSport} maxLength={60}
                      onChange={(e) => setForm({ ...form, customSport: e.target.value })}
                      placeholder={lang === "fr" ? "Précisez votre sport" : "Specify your sport"}
                      data-testid="forfaits-input-sport-custom"
                      className="bg-ink border border-gold/40 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  )}
                  <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    placeholder={ts("forfaits.form_date")} data-testid="forfaits-input-date" type="date"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none text-ivory/80" />
                  <button type="submit" disabled={busy}
                    data-testid="forfaits-submit"
                    className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold py-4 rounded-full text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {busy ? t("forfaits.form_sending") : t("forfaits.form_send")}
                  </button>
                </form>
              </>
            )}
          </div>
        </FadeUp>

        <p className="text-center text-xs text-ivory/40 mt-8">
          {lang === "fr" ? "Une question ? " : "Questions? "}
          <ELink editPath="link.forfaits_contact" to="/contact" className="text-gold hover:underline">{t("faq.cta_btn")}</ELink>
        </p>

        {/* Même section CTA que la page Les Catalogues (demande du propriétaire,
            2026-10-17) : Parlons-en + gros bouton Soumission 30 s + 4 canaux —
            la pastille flottante « Soumission en 30 s » est masquée sur CETTE page
            (elle fait double emploi ici, voir QuickQuoteFab dans App.js) */}
        <FadeUp className="mt-14">
          <div className="border border-gold/25 rounded-2xl p-10 md:p-14 text-center spotlight" data-testid="forfaits-journey">
            <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("catalogues.custom_kicker")}</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-4">{t("catalogues.custom_title")}</h2>
            <p className="text-ivory/60 max-w-xl mx-auto mb-8">{t("catalogues.custom_sub")}</p>
            <div className="mb-10">
              <Link
                to="/soumission-rapide" data-testid="forfaits-quick-quote"
                onClick={() => trackClicSoumission("forfaits")}
                className="group inline-flex items-center gap-3 bg-gold text-ink font-bold text-lg md:text-xl px-10 md:px-14 py-5 rounded-full shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:bg-champagne hover:shadow-[0_0_55px_rgba(212,175,55,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
              >
                <Zap className="w-6 h-6 transition-transform duration-300 group-hover:-rotate-12" />
                {t("quote.fab_long")}
                <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://m.me/trophee.ca" target="_blank" rel="noreferrer" data-testid="forfaits-messenger"
                className="group inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
              >
                <MessageCircle className="w-5 h-5" /> Messenger
              </a>
              <a
                href="sms:+15145656665" data-testid="forfaits-texto"
                className="inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <MessageSquare className="w-5 h-5" /> {t("footer.texto")}
              </a>
              <CallRequestDialog testid="forfaits-call"
                className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300" />
              <Link
                to="/contact" data-testid="forfaits-form-lien"
                className="inline-flex items-center gap-3 border border-ivory/25 text-ivory px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <ClipboardList className="w-5 h-5" /> {t("catalogues.custom_form")}
              </Link>
            </div>
          </div>
        </FadeUp>
      </div>

      {/* Popup de la fiche boutique — au clic sur une miniature de forfait */}
      {popup && (
        <div className="fixed inset-0 z-[80] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setPopup(null)} data-testid="forfait-product-popup">
          <div className="bg-surface border border-gold/30 rounded-2xl p-6 max-w-sm w-full relative shadow-[0_0_40px_rgba(212,175,55,0.15)]"
            onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setPopup(null)} data-testid="forfait-product-popup-close"
              className="absolute top-3 right-3 text-ivory/50 hover:text-gold transition-colors duration-200 cursor-pointer z-10">
              <X className="w-5 h-5" />
            </button>
            <div className="bg-white rounded-xl aspect-square flex items-center justify-center p-5 mb-5">
              <img src={popup.image} alt={lang === "en" ? (popup.name_en || popup.name) : popup.name}
                className="max-h-full max-w-full object-contain" />
            </div>
            <p className="font-semibold text-ivory leading-snug mb-1" data-testid="forfait-product-popup-name">
              {lang === "en" ? (popup.name_en || popup.name) : popup.name}
            </p>
            {popup.base_price > 0 && (
              <p className="font-serif text-2xl text-gold mb-5" data-testid="forfait-product-popup-price">
                {t("catalogue.grid_from", { price: fmt(popup.base_price) })}
              </p>
            )}
            {popup.id && (
              <Link to={`/produit/${popup.id}`} data-testid="forfait-product-popup-link"
                className="inline-flex items-center justify-center gap-2 w-full bg-gold text-ink font-bold py-3.5 rounded-full text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all duration-300">
                {lang === "fr" ? "Voir la fiche boutique" : "View shop page"} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
